import { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, Loader2, CheckCircle } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fM = (v) => v ? `${v.toFixed(1)}` : "0";
const fPct = (v) => v ? `${v.toFixed(1)}%` : "—";

export default function FinancialExport() {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [xlLoading, setXlLoading] = useState(false);
  const [pdfDone, setPdfDone] = useState(false);
  const [xlDone, setXlDone] = useState(false);

  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const [pnlRes, chRes, revRes, projRes] = await Promise.all([
        axios.get(`${API}/financial/pnl`),
        axios.get(`${API}/financial/channel-pnl`),
        axios.get(`${API}/financial/revenue-cuts`),
        axios.get(`${API}/financial/projections`),
      ]);
      const pnl = pnlRes.data;
      const channels = chRes.data;
      const revCuts = revRes.data;
      const proj = projRes.data;
      const d = pnl.data || {};
      const labels = pnl.labels || [];
      const periods = pnl.periods || [];

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 16;
      const CW = W - M * 2;
      const C = {
        navy: [2, 6, 23], slate900: [15, 23, 42], slate800: [30, 41, 59],
        blue: [59, 130, 246], emerald: [16, 185, 129], amber: [245, 158, 11],
        white: [248, 250, 252], gray: [148, 163, 184],
        amberBg: [30, 22, 5], amberBorder: [120, 80, 10], amberText: [252, 211, 77],
      };

      let pageNum = 0;
      const addPage = (title, sub) => {
        doc.addPage(); pageNum++;
        doc.setFillColor(...C.navy); doc.rect(0, 0, W, H, "F");
        doc.setFillColor(...C.slate800); doc.rect(0, 0, W, 13, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...C.gray);
        doc.text("STARANDDAISY  |  FINANCIAL MODEL", M, 8.5);
        doc.text(`Page ${pageNum}`, W - M, 8.5, { align: "right" });
        doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...C.white);
        doc.text(title, M, 26);
        if (sub) { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...C.gray); doc.text(sub, M, 32); }
        doc.setDrawColor(...C.slate800); doc.line(M, sub ? 35 : 30, W - M, sub ? 35 : 30);
        doc.setFontSize(6.5); doc.setTextColor(80, 90, 110);
        doc.text("Confidential", M, H - 8);
        return sub ? 40 : 35;
      };

      const annot = (y, text) => {
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5);
        const lines = doc.splitTextToSize(text, CW - 14);
        const bH = lines.length * 3.2 + 6;
        doc.setFillColor(...C.amberBg); doc.roundedRect(M, y, CW, bH, 1.5, 1.5, "F");
        doc.setDrawColor(...C.amberBorder); doc.roundedRect(M, y, CW, bH, 1.5, 1.5, "S");
        doc.setFillColor(...C.amber); doc.rect(M, y, 2, bH, "F");
        doc.setTextColor(...C.amberText); doc.text(lines, M + 7, y + 4.5);
        return y + bH + 3;
      };

      const ts = {
        styles: { fontSize: 6.5, cellPadding: 2, textColor: [200, 210, 220], fillColor: C.slate900, lineColor: C.slate800, lineWidth: 0.2 },
        headStyles: { fillColor: C.slate800, textColor: C.gray, fontStyle: "bold", fontSize: 6 },
        alternateRowStyles: { fillColor: [10, 18, 35] }, theme: "grid", margin: { left: M, right: M },
      };

      // Cover
      doc.setFillColor(...C.navy); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...C.emerald); doc.rect(0, 0, 4, H, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(36); doc.setTextColor(...C.white);
      doc.text("StarAndDaisy", M + 6, 55);
      doc.setFont("helvetica", "normal"); doc.setFontSize(12); doc.setTextColor(...C.gray);
      doc.text("FINANCIAL MODEL REPORT", M + 6, 67);
      doc.setFontSize(9); doc.text("FY2024A to FY2030E  |  INR Millions", M + 6, 80);
      doc.setFontSize(7); doc.setTextColor(80, 90, 110);
      doc.text("Confidential  |  Prepared for Investor Review", M + 6, H - 15);

      // P&L
      let y = addPage("Consolidated P&L Statement", "FY2024A to FY2030E  |  INR Millions");
      const pnlRows = [
        ["Net Revenue", "net_revenue", true], ["COGS", "cogs"], ["Gross Profit", "gross_profit", true],
        ["Gross Margin %", "gross_margin", false, true], ["Freight", "freight"], ["CM1", "cm1", true],
        ["CM1 Margin %", "cm1_margin", false, true], ["Advertising", "advertising"],
        ["Mktplace Commission", "marketplace_commission"], ["CM2", "cm2", true],
        ["CM2 Margin %", "cm2_margin", false, true], ["Employee Expense", "employee_expense"],
        ["EBITDA", "ebitda", true], ["EBITDA Margin %", "ebitda_margin", false, true],
        ["PBT", "pbt", true], ["PAT", "pat", true], ["PAT Margin %", "pat_margin", false, true],
      ];
      autoTable(doc, {
        head: [["Particulars", ...labels]],
        body: pnlRows.map(([label, key, bold, pct]) =>
          [label, ...periods.map((p) => pct ? fPct(d[key]?.[p]) : d[key]?.[p] ? fM(d[key][p]) : "—")]
        ),
        startY: y, ...ts,
      });
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 80) + 4;
      y = annot(y, `Revenue projected from INR ${fM(d.net_revenue?.FY2025A)}M (FY25) to INR ${fM(d.net_revenue?.FY2030E)}M (FY30) — ~35% CAGR. EBITDA margin expands from ${fPct(d.ebitda_margin?.FY2025A)} to ${fPct(d.ebitda_margin?.FY2030E)} driven by operating leverage and D2C channel growth.`);

      // Channel P&L
      y = addPage("Channel P&L Summary", "Net Revenue & CM2 by Channel  |  INR Millions");
      const chData = channels.data || [];
      autoTable(doc, {
        head: [["Channel", ...labels.map(l => `NR ${l}`)]],
        body: chData.map((ch) => [ch.channel, ...periods.map((p) => fM(ch.net_revenue?.[p]))]),
        startY: y, ...ts,
      });
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 40) + 3;
      autoTable(doc, {
        head: [["Channel", ...labels.map(l => `CM2% ${l}`)]],
        body: chData.map((ch) => [ch.channel, ...periods.map((p) => fPct(ch.cm2_margin?.[p]))]),
        startY: y, ...ts,
      });
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 40) + 4;
      y = annot(y, "Website/App is the highest-margin channel (zero marketplace commission, no returns) projected to become the single largest revenue contributor by FY30. All 8 channels covered: Amazon, Flipkart, Website, FirstCry, Meesho, Myntra, Jio, Blinkit.");

      // Revenue Cuts
      y = addPage("Revenue Breakdown", "Net Revenue by Category & Top Products  |  FY24-FY25");
      const cats = revCuts.by_category || [];
      autoTable(doc, {
        head: [["Category", "FY24", "FY25", "Growth"]],
        body: cats.map((c) => [c.category, fM(c.FY2024A), fM(c.FY2025A), `${c.growth > 0 ? "+" : ""}${c.growth}%`]),
        startY: y, ...ts,
      });
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 60) + 3;
      const prods = (revCuts.top_products || []).slice(0, 10);
      autoTable(doc, {
        head: [["#", "Product", "Category", "FY25", "Growth"]],
        body: prods.map((p, i) => [i + 1, p.product, p.category, fM(p.FY2025A), `${p.growth > 0 ? "+" : ""}${p.growth}%`]),
        startY: y, ...ts,
      });

      // Projections
      y = addPage("5-Year Projections", "Revenue, EBITDA & PAT Trajectory");
      const projD = proj.data || {};
      autoTable(doc, {
        head: [["Metric", ...(projD.periods || [])]],
        body: [
          ["Net Revenue", ...(projD.net_revenue || []).map(v => `${v.toFixed(0)}M`)],
          ["Gross Profit", ...(projD.gross_profit || []).map(v => `${v.toFixed(0)}M`)],
          ["EBITDA", ...(projD.ebitda || []).map(v => `${v.toFixed(0)}M`)],
          ["PAT", ...(projD.pat || []).map(v => `${v.toFixed(0)}M`)],
          ["Gross Margin", ...(projD.gross_margin || []).map(v => `${v.toFixed(1)}%`)],
          ["EBITDA Margin", ...(projD.ebitda_margin || []).map(v => `${v.toFixed(1)}%`)],
          ["PAT Margin", ...(projD.pat_margin || []).map(v => `${v.toFixed(1)}%`)],
        ],
        startY: y, ...ts,
      });
      y = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || y + 50) + 4;
      y = annot(y, `3-Year Revenue CAGR: ${projD.revenue_cagr_3y}%. 5-Year Revenue CAGR: ${projD.revenue_cagr_5y}%. Growth driven by scaling D2C website, offline expansion, new product launches (FY28+), and export market entry. EBITDA margin expands as fixed-cost leverage kicks in at scale.`);

      doc.save("StarAndDaisy_Financial_Model.pdf");
      setPdfDone(true);
    } catch (e) {
      console.error("PDF error:", e);
    } finally {
      setPdfLoading(false);
    }
  };

  const generateExcel = async () => {
    setXlLoading(true);
    try {
      const [pnlRes, chRes, revRes, projRes] = await Promise.all([
        axios.get(`${API}/financial/pnl`),
        axios.get(`${API}/financial/channel-pnl`),
        axios.get(`${API}/financial/revenue-cuts`),
        axios.get(`${API}/financial/projections`),
      ]);

      const pnl = pnlRes.data;
      const channels = chRes.data;
      const revCuts = revRes.data;
      const proj = projRes.data;
      const d = pnl.data || {};
      const labels = pnl.labels || [];
      const periods = pnl.periods || [];

      // Build CSV content for each sheet, then combine as multi-sheet CSV download
      const buildCSV = (headers, rows) => {
        const escape = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
        return [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
      };

      // P&L Sheet
      const pnlRows = [
        ["Gross Revenue", "gross_revenue"], ["Online", "online_revenue"], ["Offline", "offline_revenue"],
        ["Returns", "returns"], ["Net Revenue", "net_revenue"], ["COGS", "cogs"],
        ["Gross Profit", "gross_profit"], ["Gross Margin %", "gross_margin"],
        ["Freight", "freight"], ["CM1", "cm1"], ["CM1 Margin %", "cm1_margin"],
        ["Advertising", "advertising"], ["Marketplace Commission", "marketplace_commission"],
        ["CM2", "cm2"], ["CM2 Margin %", "cm2_margin"],
        ["Employee Expense", "employee_expense"], ["EBITDA", "ebitda"],
        ["EBITDA Margin %", "ebitda_margin"], ["Depreciation", "depreciation"],
        ["Interest", "interest"], ["PBT", "pbt"], ["Tax", "tax"], ["PAT", "pat"],
        ["PAT Margin %", "pat_margin"],
      ];
      const pnlCSV = buildCSV(
        ["Particulars (INR Millions)", ...labels],
        pnlRows.map(([label, key]) => [
          label,
          ...periods.map((p) => key.includes("margin") ? fPct(d[key]?.[p]) : (d[key]?.[p]?.toFixed(2) || ""))
        ])
      );

      // Channel P&L Sheet
      const chRows = [];
      (channels.data || []).forEach((ch) => {
        chRows.push([ch.channel, "", "", "", "", "", "", ""]);
        chRows.push(["  Net Revenue", ...periods.map((p) => ch.net_revenue?.[p]?.toFixed(2) || "")]);
        chRows.push(["  Gross Profit", ...periods.map((p) => ch.gross_profit?.[p]?.toFixed(2) || "")]);
        chRows.push(["  CM1", ...periods.map((p) => ch.cm1?.[p]?.toFixed(2) || "")]);
        chRows.push(["  CM2", ...periods.map((p) => ch.cm2?.[p]?.toFixed(2) || "")]);
        chRows.push(["  CM2 Margin", ...periods.map((p) => fPct(ch.cm2_margin?.[p]))]);
        chRows.push(["", "", "", "", "", "", "", ""]);
      });
      const chCSV = buildCSV(["Channel P&L (INR M)", ...labels], chRows);

      // Revenue by Category
      const catCSV = buildCSV(
        ["Category", "FY24A", "FY25A", "YoY Growth"],
        (revCuts.by_category || []).map((c) => [c.category, c.FY2024A?.toFixed(2) || "", c.FY2025A?.toFixed(2) || "", `${c.growth}%`])
      );

      // Projections
      const projD = proj.data || {};
      const projCSV = buildCSV(
        ["Metric", ...(projD.periods || [])],
        [
          ["Net Revenue (M)", ...(projD.net_revenue || []).map(v => v?.toFixed(2))],
          ["Gross Profit (M)", ...(projD.gross_profit || []).map(v => v?.toFixed(2))],
          ["EBITDA (M)", ...(projD.ebitda || []).map(v => v?.toFixed(2))],
          ["PAT (M)", ...(projD.pat || []).map(v => v?.toFixed(2))],
          ["Gross Margin %", ...(projD.gross_margin || []).map(v => `${v?.toFixed(1)}%`)],
          ["EBITDA Margin %", ...(projD.ebitda_margin || []).map(v => `${v?.toFixed(1)}%`)],
          ["PAT Margin %", ...(projD.pat_margin || []).map(v => `${v?.toFixed(1)}%`)],
        ]
      );

      // Combine all sheets into one download
      const fullCSV = [
        "CONSOLIDATED P&L STATEMENT", "", pnlCSV, "", "",
        "CHANNEL P&L", "", chCSV, "", "",
        "REVENUE BY CATEGORY", "", catCSV, "", "",
        "5-YEAR PROJECTIONS", "", projCSV
      ].join("\n");

      const blob = new Blob([fullCSV], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "StarAndDaisy_Financial_Model.csv";
      a.click();
      URL.revokeObjectURL(url);
      setXlDone(true);
    } catch (e) {
      console.error("Excel error:", e);
    } finally {
      setXlLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="financial-export-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Financial Model Export</h1>
        <p className="text-sm text-slate-400 mt-1">Download P&L, Channel Economics, Revenue Breakdown &amp; Projections</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* PDF Export */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="fin-pdf-card">
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 ${pdfDone ? "bg-emerald-950/50 border border-emerald-800" : "bg-slate-800/80 border border-slate-700"}`}>
              {pdfDone ? <CheckCircle className="w-8 h-8 text-emerald-400" /> : <FileDown className="w-8 h-8 text-rose-400" />}
            </div>
            <h3 className="font-space text-base font-semibold text-slate-200 mb-1">PDF Report</h3>
            <p className="text-xs text-slate-400 max-w-sm leading-relaxed mb-5">
              Corporate-style PDF with P&L, Channel P&L, Revenue Breakdown, 5-Year Projections — all with investor annotations.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5 text-[10px] text-slate-500 justify-center">
              {["P&L", "Channel P&L", "Revenue Cuts", "Projections"].map((s) => (
                <span key={s} className="px-2 py-0.5 border border-slate-700 rounded bg-slate-800/50">{s}</span>
              ))}
            </div>
            <Button onClick={generatePDF} disabled={pdfLoading} className="bg-rose-600 hover:bg-rose-500 text-white text-sm px-6" data-testid="fin-pdf-btn">
              {pdfLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><FileDown className="w-4 h-4 mr-2" />{pdfDone ? "Download Again" : "Download PDF"}</>}
            </Button>
          </CardContent>
        </Card>

        {/* Excel Export */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="fin-excel-card">
          <CardContent className="flex flex-col items-center justify-center py-14 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 ${xlDone ? "bg-emerald-950/50 border border-emerald-800" : "bg-slate-800/80 border border-slate-700"}`}>
              {xlDone ? <CheckCircle className="w-8 h-8 text-emerald-400" /> : <FileSpreadsheet className="w-8 h-8 text-emerald-400" />}
            </div>
            <h3 className="font-space text-base font-semibold text-slate-200 mb-1">Excel / CSV Export</h3>
            <p className="text-xs text-slate-400 max-w-sm leading-relaxed mb-5">
              Structured spreadsheet with P&L, Channel P&L (all 8 channels), Revenue by Category, and Projections — ready for your own analysis.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-5 text-[10px] text-slate-500 justify-center">
              {["Consol. P&L", "8 Channels", "15 Categories", "FY24-FY30"].map((s) => (
                <span key={s} className="px-2 py-0.5 border border-slate-700 rounded bg-slate-800/50">{s}</span>
              ))}
            </div>
            <Button onClick={generateExcel} disabled={xlLoading} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm px-6" data-testid="fin-excel-btn">
              {xlLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><FileSpreadsheet className="w-4 h-4 mr-2" />{xlDone ? "Download Again" : "Download Excel"}</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
